/**
 * Helpers compartidos para Experience Presets (Wave 2).
 */
import type {
  AnnouncementItem,
  ExperienceBrand,
  ExperienceBrandColors,
  ExperienceFontRef,
  ExperienceIcon,
  ExperienceLink,
  CatalogLandingConfig,
  ExperiencePresetId,
  NavItem,
  ExperienceStaticPage,
  TenantExperienceDocument,
  ValuePropsBlockConfig,
} from "../types";
import { EXPERIENCE_SCHEMA_VERSION } from "../types";

export const TYPE_SCALE = {
  xs: "0.75rem",
  sm: "0.875rem",
  md: "1rem",
  lg: "1.125rem",
  xl: "1.25rem",
  "2xl": "1.5rem",
  "3xl": "1.875rem",
  display: "2.5rem",
} as const;

export function font(
  family: string,
  fallbacks: string[] = ["system-ui", "sans-serif"],
  source: ExperienceFontRef["source"] = "system",
): ExperienceFontRef {
  return { family, fallbacks, source };
}

function baseBrandColors(): ExperienceBrandColors {
  return {
    primary: "#1a1a1a",
    secondary: "#666666",
    accent: "#1a1a1a",
    background: "#ffffff",
    surface: "#f7f7f7",
    text: "#1a1a1a",
    textMuted: "#666666",
    border: "#e5e5e5",
    onPrimary: "#ffffff",
    onMedia: "#ffffff",
  };
}

export function baseBrand(partial?: Partial<ExperienceBrand>): ExperienceBrand {
  const base: ExperienceBrand = {
    name: "Mi tienda",
    logo: {
      light: { kind: "image", src: "", alt: "Logo" },
      width: 120,
    },
    colors: baseBrandColors(),
    typography: {
      heading: font("Georgia", ["Times New Roman", "serif"]),
      body: font("system-ui"),
      scale: { ...TYPE_SCALE },
      bodyLineHeight: 1.5,
    },
    shape: {
      radius: { token: "md" },
      shadow: { token: "sm" },
      controlHeight: "md",
    },
    iconography: { set: "lucide", strokeWidth: 1.75 },
    spacing: {
      sectionY: "3rem",
      blockGap: "2rem",
      containerMax: "72rem",
      gutter: "1rem",
    },
    density: "comfortable",
    theme: { mode: "light", allowShopperToggle: false },
    animation: { enabled: true, intensity: "subtle", preferReducedMotion: "respect" },
  };
  if (!partial) return base;
  return {
    ...base,
    ...partial,
    logo: { ...base.logo, ...partial.logo },
    colors: { ...base.colors, ...partial.colors },
    typography: {
      ...base.typography,
      ...partial.typography,
      scale: partial.typography?.scale ?? base.typography.scale,
      heading: partial.typography?.heading ?? base.typography.heading,
      body: partial.typography?.body ?? base.typography.body,
    },
    shape: { ...base.shape, ...partial.shape },
    iconography: { ...base.iconography, ...partial.iconography },
    spacing: { ...base.spacing, ...partial.spacing },
    theme: { ...base.theme, ...partial.theme },
    animation: { ...base.animation, ...partial.animation },
  };
}

/** Documento base — punto de partida para presets y migración legacy. */
export function createEmptyExperienceDocument(
  overrides?: Partial<TenantExperienceDocument>,
): TenantExperienceDocument {
  const doc: TenantExperienceDocument = {
    schemaVersion: EXPERIENCE_SCHEMA_VERSION,
    brand: baseBrand(),
    announcement: {
      enabled: false,
      rotation: "fade",
      speedMs: 5000,
      stackMode: "rotate",
      items: [],
    },
    navigation: {
      items: classicStorefrontNav(),
      megaMenu: { enabled: true, columns: 4, showImages: true, showFeatured: true },
    },
    header: {
      layout: "classic",
      sticky: true,
      transparentOverHero: false,
      compact: false,
      height: "md",
      logoPosition: "left",
      show: {
        search: true,
        account: true,
        wishlist: false,
        cart: true,
        collections: false,
        brands: false,
        promotions: false,
        megaMenu: true,
      },
      search: { variant: "inline", predictive: false },
      cart: { variant: "icon_badge" },
      mobile: { menuVariant: "drawer", showBottomSafeOffset: true },
    },
    home: { blocks: [] },
    plp: {
      layout: "grid",
      defaultSort: "recommended",
      pageSize: 24,
      pagination: "load_more",
      breadcrumb: true,
      hero: { enabled: true, showDescription: true, showBanner: false },
      toolbar: {
        sticky: true,
        showCount: true,
        showSort: true,
        showViewToggle: true,
        showDensity: false,
        showFilterButton: true,
      },
      sidebar: { enabled: true, position: "left", sticky: true },
      mobileFilters: { variant: "drawer" },
      filters: {
        enabled: ["category", "brand", "price", "stock", "color", "size", "promo", "tag", "collection"],
        showChips: true,
        showClearAll: true,
      },
      grid: {
        columns: { desktop: 5, tablet: 3, mobile: 2 },
        gap: "compact",
        cardStyle: "inherit",
      },
      seo: { showFooterCopy: true },
      newsletter: { enabled: false },
      landingDefaults: {
        category: { enabled: true, hero: { enabled: true }, banner: { enabled: false }, seo: {} },
        collection: { enabled: true, hero: { enabled: true }, banner: { enabled: false }, seo: {} },
        brand: { enabled: true, hero: { enabled: true }, banner: { enabled: false }, seo: {} },
      },
      landings: [],
    },
    pdp: {
      gallery: {
        layout: "thumbs_bottom",
        zoom: true,
        video: true,
        stickyMediaDesktop: true,
        ratio: "auto",
      },
      buyBox: {
        stickyMobile: true,
        stickyDesktop: true,
        showTrust: true,
        showSku: false,
      },
      variants: {
        colorDisplay: "swatch",
        sizeDisplay: "grid",
        showSizeGuide: false,
        sizeGuideMode: "modal",
        allowColorAndSizeTogether: true,
      },
      content: { descriptionMode: "accordion" },
      merchandising: {
        related: true,
        upsell: false,
        crossSell: false,
        bundles: false,
        complementary: false,
      },
      trust: { itemsSource: "preset" },
    },
    checkout: {
      density: "comfortable",
      showOrderSummarySticky: true,
      showTrustBadges: true,
      accentFromBrand: true,
      success: { showConfetti: false },
    },
    footer: {
      layout: "columns",
      columns: [],
      showNewsletter: false,
      showSocial: true,
      showLocations: true,
      showPaymentIcons: true,
      showPolicyLinks: true,
      showMap: false,
      showStructuraBadge: true,
    },
    mobile: {
      bottomNav: {
        enabled: true,
        items: ["home", "search", "categories", "cart"],
        showCartSummaryBar: true,
      },
      fab: { enabled: true, kind: "whatsapp", hideOnCheckout: true },
      stickyCta: { pdp: true, cart: true },
      quickSearch: true,
      quickCart: true,
    },
    seo: {
      titleTemplate: "{page} · {shop}",
      collectionSeo: { useDescriptionAsCopy: true },
      productSeo: { includePriceInTitle: false },
    },
    motion: {
      enabled: true,
      pageTransition: "none",
      hoverLift: true,
      cartFeedback: "pulse",
      reduceMotionHonor: true,
    },
    commercePresentation: {
      showPromoBadges: true,
      showDiscountPercentage: true,
      showStockLabel: false,
      showPrepTime: false,
      priceDisplay: "exact",
      quickAdd: true,
      stickyCartBar: false,
      brandsNav: false,
    },
    pages: defaultStaticPages(),
    compliance: {
      ageGate: {
        enabled: false,
        minAge: 18,
        policyVersion: "1",
        title: "¿Sos mayor de edad?",
        message:
          "Para entrar a esta tienda tenés que declarar que sos mayor de 18 años. Prohibida la venta de alcohol a menores.",
        confirmLabel: "Sí, soy mayor de 18",
        denyLabel: "No, soy menor",
        denyMessage: "No podemos mostrarte este catálogo. Volvé cuando seas mayor de edad.",
        rememberDays: 30,
        recordAttestation: true,
        legalNote:
          "Al continuar declarás bajo tu responsabilidad que tenés la edad legal para comprar bebidas alcohólicas. Esta confirmación no verifica tu identidad.",
      },
      leadCapture: {
        enabled: false,
        delayMs: 1200,
        rememberDays: 90,
        title: "¡Conocé nuestras novedades y descuentos exclusivos!",
        emailPlaceholder: "Email",
        namePlaceholder: "Nombre",
        showName: true,
        showBirthDate: false,
        birthDatePlaceholder: "DD-MM-AAAA",
        ctaLabel: "Suscribirme",
        successMessage: "¡Listo! Ya estás en la lista.",
        footnote: "Recibirás un correo para validar tu email.",
        legalNote: "Al suscribirte aceptás recibir novedades del comercio. Podés darte de baja cuando quieras.",
      },
      cookieConsent: {
        enabled: false,
        rememberDays: 180,
        title: "Usamos cookies",
        message:
          "Usamos cookies propias para que la tienda funcione y recordar tus preferencias.",
        acceptLabel: "Aceptar todo",
        rejectLabel: "Rechazar",
        customizeLabel: "Personalizar",
        policyLinkLabel: "Política de privacidad",
        categories: { essential: true, analytics: false, marketing: false },
      },
    },
  };

  return { ...doc, ...overrides, schemaVersion: EXPERIENCE_SCHEMA_VERSION };
}

function ensureDefaultStaticPages(existing?: ExperienceStaticPage[]): ExperienceStaticPage[] {
  const defaults = defaultStaticPages();
  const bySlug = new Map((existing ?? []).map((page) => [page.slug, page]));
  for (const page of defaults) {
    if (!bySlug.has(page.slug)) bySlug.set(page.slug, page);
  }
  return defaults.map((page) => bySlug.get(page.slug) ?? page);
}

/**
 * Defaults estructurales del storefront — se aplican siempre (onboarding, presets, demos).
 * Nav clásica, mega menú de categorías, páginas informativas y grilla más densa.
 */
export function applyStorefrontDefaults(doc: TenantExperienceDocument): TenantExperienceDocument {
  const isServiciosStudio = doc.presetId === "servicios_studio";

  if (isServiciosStudio) {
    doc.navigation = {
      ...doc.navigation,
      items: doc.navigation?.items?.length ? doc.navigation.items : serviciosStudioNav(),
      megaMenu: {
        ...doc.navigation?.megaMenu,
        enabled: false,
        columns: doc.navigation?.megaMenu?.columns ?? 3,
        showImages: false,
        showFeatured: false,
      },
    };
    doc.header = {
      ...doc.header,
      show: {
        ...doc.header.show,
        megaMenu: false,
        cart: false,
        search: false,
        collections: false,
        brands: false,
        promotions: false,
      },
      search: { ...doc.header.search, variant: "hidden", predictive: false },
    };
    doc.mobile = {
      ...doc.mobile,
      bottomNav: {
        enabled: true,
        items: ["home"],
        showCartSummaryBar: false,
      },
      stickyCta: { pdp: false, cart: false },
      quickSearch: false,
      quickCart: false,
    };
    doc.commercePresentation = {
      ...doc.commercePresentation,
      showPromoBadges: false,
      quickAdd: false,
      stickyCartBar: false,
    };
  } else {
    doc.navigation = {
      ...doc.navigation,
      items: classicStorefrontNav(),
      megaMenu: {
        ...doc.navigation?.megaMenu,
        enabled: true,
        columns: doc.navigation?.megaMenu?.columns ?? 4,
        showImages: doc.navigation?.megaMenu?.showImages ?? true,
        showFeatured: doc.navigation?.megaMenu?.showFeatured ?? true,
      },
    };

    doc.header = {
      ...doc.header,
      show: {
        ...doc.header.show,
        megaMenu: true,
      },
    };

    doc.plp = {
      ...doc.plp,
      grid: {
        ...doc.plp.grid,
        columns: { desktop: 5, tablet: 3, mobile: 2 },
        gap: "compact",
        cardStyle: doc.plp.grid?.cardStyle ?? "inherit",
      },
    };
  }

  doc.pages = ensureDefaultStaticPages(doc.pages);

  doc.footer = {
    ...doc.footer,
    showSocial: doc.footer.showSocial ?? true,
    showPolicyLinks: doc.footer.showPolicyLinks ?? true,
  };

  doc.home = {
    ...doc.home,
    blocks: doc.home.blocks.filter((block) => block.type !== "catalog"),
  };

  return doc;
}

export function withHomeBlocks(
  presetId: ExperiencePresetId,
  brand: Partial<ExperienceBrand>,
  blockOrders: TenantExperienceDocument["home"]["blocks"],
  extra?: Partial<TenantExperienceDocument>,
): TenantExperienceDocument {
  return applyStorefrontDefaults(
    createEmptyExperienceDocument({
      presetId,
      brand: baseBrand(brand),
      home: { blocks: blockOrders },
      ...extra,
    }),
  );
}

export type HeroSlideInput = {
  id?: string;
  headline: string;
  subheadline?: string;
  badge?: string;
  cta?: string;
  mediaSrc?: string;
  overlay?: number;
};

/** Hero carrusel de nicho: ≥2 slides, copy centrado abajo (no texto a la izquierda). */
export const heroCarousel = (
  order: number,
  slides: HeroSlideInput[],
  opts?: {
    desktop?: string;
    mobile?: string;
    overlay?: number;
    intervalMs?: number;
  },
) => {
  const list = slides.length >= 2 ? slides : [...slides, { headline: slides[0]?.headline ?? "Nuestra tienda" }];
  return {
    id: "hero-main",
    type: "hero" as const,
    enabled: true,
    order,
    config: {
      variant: "simple" as const,
      height: {
        desktop: opts?.desktop ?? "68svh",
        mobile: opts?.mobile ?? "62svh",
      },
      autoplay: true,
      intervalMs: opts?.intervalMs ?? 5500,
      showDots: true,
      showArrows: true,
      slides: list.map((s, i) => ({
        id: s.id ?? `slide-${i + 1}`,
        enabled: true,
        media: s.mediaSrc ? ({ kind: "image" as const, src: s.mediaSrc }) : undefined,
        overlay: { color: "#000000", opacity: s.overlay ?? opts?.overlay ?? 0.42 },
        headline: s.headline,
        subheadline: s.subheadline,
        badge: s.badge,
        alignment: "center" as const,
        contentPosition: "bottom" as const,
        cta1: {
          label: s.cta ?? "Ver catálogo",
          link: { scrollTo: "catalogo" },
          style: "primary" as const,
        },
      })),
    },
  } satisfies TenantExperienceDocument["home"]["blocks"][number];
};

export const heroSimple = (
  order: number,
  headline: string,
  opts?: {
    desktop?: string;
    mobile?: string;
    cta?: string;
    overlay?: number;
    subheadline?: string;
    badge?: string;
  },
) =>
  heroCarousel(
    order,
    [
      {
        headline,
        subheadline: opts?.subheadline,
        badge: opts?.badge,
        cta: opts?.cta,
        overlay: opts?.overlay,
      },
      {
        id: "slide-2",
        headline: opts?.subheadline ? headline : "Pedí online",
        subheadline: opts?.subheadline ?? "Envíos y retiro según disponibilidad de la tienda.",
        cta: opts?.cta ?? "Explorar",
        overlay: opts?.overlay,
      },
      {
        id: "slide-3",
        headline: "Beneficios de la casa",
        subheadline: "Promos, destacados y atención cercana.",
        cta: "Ver ofertas",
        overlay: opts?.overlay,
      },
    ],
    { desktop: opts?.desktop, mobile: opts?.mobile, overlay: opts?.overlay },
  );

export function ann(
  id: string,
  text: string,
  colors: { background: string; text: string; cta?: string },
  opts?: {
    priority?: number;
    ctaLabel?: string;
    link?: ExperienceLink;
  },
): AnnouncementItem {
  return {
    id,
    enabled: true,
    priority: opts?.priority ?? 1,
    text,
    colors,
    ...(opts?.ctaLabel
      ? {
          cta: {
            label: opts.ctaLabel,
            link: opts.link ?? { page: "plp" as const },
            style: "link" as const,
          },
        }
      : {}),
    ...(opts?.link && !opts?.ctaLabel ? { link: opts.link } : {}),
  };
}

export function nav(id: string, label: string, link?: ExperienceLink, badge?: string): NavItem {
  return {
    id,
    label,
    link: link ?? { page: "plp" },
    ...(badge ? { badge } : {}),
  };
}

/** Nav clásica de ecommerce: jerarquía fija + Categorías abre mega menú. */
export function classicStorefrontNav(): NavItem[] {
  return [
    nav("nav-home", "Inicio", { page: "home" }),
    nav("nav-products", "Productos", { href: "/catalogo" }),
    nav("nav-categories", "Categorías", { href: "#categorias" }),
    nav("nav-how", "Cómo comprar", { href: "/pagina/como-comprar" }),
    nav("nav-returns", "Devoluciones", { href: "/pagina/devoluciones" }),
    nav("nav-contact", "Contacto", { href: "/pagina/contacto" }),
  ];
}

/**
 * Nav del sitio de turnos. `sellsProducts` suma la tienda para los estudios
 * que además venden productos (shampoo, tratamientos, etc.).
 */
export function serviciosStudioNav(opts: { sellsProducts?: boolean } = {}): NavItem[] {
  return [
    nav("nav-home", "Inicio", { page: "home" }),
    nav("nav-services", "Servicios", { href: "#servicios" }),
    ...(opts.sellsProducts ? [nav("nav-shop", "Tienda", { href: "/catalogo" })] : []),
    nav("nav-arrive", "Cómo llegar", { href: "#ubicacion" }),
  ];
}

export function defaultStaticPages(): ExperienceStaticPage[] {
  return [
    {
      id: "page-como-comprar",
      slug: "como-comprar",
      title: "Cómo comprar",
      enabled: true,
      html: `<h2>Cómo comprar</h2>
<p>Elegí productos del catálogo, agregalos al carrito y completá el checkout con tus datos de entrega o retiro.</p>
<ol>
<li>Explorá categorías o buscá por nombre.</li>
<li>Agregá al carrito y revisá cantidades.</li>
<li>Elegí envío o retiro según disponibilidad.</li>
<li>Pagá con el medio habilitado por la tienda.</li>
</ol>`,
      seo: { title: "Cómo comprar", description: "Pasos para comprar online en nuestra tienda." },
    },
    {
      id: "page-devoluciones",
      slug: "devoluciones",
      title: "Devoluciones",
      enabled: true,
      html: `<h2>Devoluciones y cambios</h2>
<p>Si recibiste un producto incorrecto o con falla, contactanos dentro de las 48 hs con tu número de pedido.</p>
<ul>
<li>Productos perecederos: revisión caso a caso.</li>
<li>Productos sellados: deben estar sin uso y con empaque original.</li>
<li>Te respondemos por WhatsApp o email con la resolución.</li>
</ul>`,
      seo: { title: "Devoluciones", description: "Política de cambios y devoluciones." },
    },
    {
      id: "page-faq",
      slug: "faq",
      title: "Preguntas frecuentes",
      enabled: true,
      html: `<h2>Preguntas frecuentes</h2>
<h3>¿Hacen envíos?</h3><p>Sí, según zona y horarios de la tienda. También podés retirar en sucursal.</p>
<h3>¿Puedo modificar mi pedido?</h3><p>Si aún no salió, escribinos lo antes posible.</p>
<h3>¿Qué medios de pago aceptan?</h3><p>Los habilitados en checkout al finalizar la compra.</p>`,
      seo: { title: "FAQ", description: "Respuestas a las consultas más comunes." },
    },
    {
      id: "page-contacto",
      slug: "contacto",
      title: "Contacto",
      enabled: true,
      html: `<h2>Contacto</h2>
<p>Escribinos por WhatsApp, teléfono o redes. Horario de atención según sucursal.</p>`,
      seo: { title: "Contacto", description: "Datos de contacto y atención al cliente." },
    },
  ];
}

export function landing(
  id: string,
  kind: CatalogLandingConfig["kind"],
  label: string,
  opts?: {
    slug?: string;
    targetId?: string;
    headline?: string;
    subheadline?: string;
    description?: string;
  },
): CatalogLandingConfig {
  const slug = opts?.slug ?? id;
  return {
    id,
    targetId: opts?.targetId ?? slug,
    kind,
    slug,
    enabled: true,
    hero: {
      enabled: true,
      headline: opts?.headline ?? label,
      subheadline: opts?.subheadline,
    },
    banner: { enabled: false },
    seo: {
      title: label,
      description: opts?.description,
    },
  };
}

export function valueItems(
  items: Array<{ title: string; text: string; icon?: string }>,
): NonNullable<ValuePropsBlockConfig["items"]> {
  return items.map((item) => ({
    title: item.title,
    text: item.text,
    ...(item.icon ? { icon: { name: item.icon } satisfies ExperienceIcon } : {}),
  }));
}

export function footerColumns(
  shopName: string,
  nicheLinks: Array<{ label: string; href?: string; page?: ExperienceLink["page"] }>,
) {
  return [
    {
      id: "shop",
      title: shopName,
      links: [
        { label: "Inicio", page: "home" as const },
        { label: "Catálogo", page: "plp" as const },
        { label: "Mi cuenta", page: "account" as const },
      ],
    },
    {
      id: "explore",
      title: "Explorar",
      links: nicheLinks.map((l) => ({
        label: l.label,
        ...(l.href ? { href: l.href } : {}),
        ...(l.page ? { page: l.page } : { page: "plp" as const }),
      })),
    },
    {
      id: "help",
      title: "Ayuda",
      links: [
        { label: "Cómo comprar", href: "/pagina/como-comprar" },
        { label: "Devoluciones", href: "/pagina/devoluciones" },
        { label: "FAQ", href: "/pagina/faq" },
        { label: "Contacto", href: "/pagina/contacto" },
      ],
    },
  ];
}
